import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { ITkModalForPostalCode } from "../../../../type/componentsType/TkModalForPostalCode";
import TkModal from "../../../atoms/TkModal";
import TkButton from "../../../atoms/TkButton";
import "../_style.scss";
import TkTable from "../../../atoms/TkTable";
import { TkRadioGroup } from "../../../atoms/TkRadioGroup";
import { AddressList } from "../../../../type/apiType/IAPI_COM013";
import HandleErrorMessage from "../../TkMessageBar";

/**
 * このコンポーネントは、郵便番号に基づいて検索結果を表示するためのモーダルです。
 * @param {boolean} open - モーダルが開いているかどうかを決定します。
 * @param {string} postcode - 検索に使用される郵便番号。
 * @param {Array} showList - 表示される住所のリスト。
 * @param {function} controlOpen - モーダルの開閉状態を制御するための関数。
 * @param {boolean} loading - ローディングアイコンが表示されるかどうかを決定します。
 * @param {function} onSuccess - ユーザーが住所を選択したときに呼び出される関数。
 * @returns {JSX.Element} - 郵便番号に基づいて検索結果を表示するモーダルコンポーネント。
 */
const ModalForPostalCode: React.FC<ITkModalForPostalCode> = (props) => {
  const {
    open,
    postcode,
    showList,
    controlOpen,
    loading,
    onSuccess,
    errorMsgId,
  } = props;
  const [selectedValue, setSelectedValue] = React.useState<AddressList>({});

  /**
   * 表示される住所のリストを取得し、コンポーネントの状態を設定します。
   */
  const resList = useMemo(() => {
    let list: any[] = [];
    showList &&
      (list = showList.map((item, index) => ({
        label: (item.address1 ?? "") + (item.address2 ?? "") + item.address3,
        value: index.toString(),
      })));
    return list;
  }, [showList]);

  const handleSelectAndClose = (flag?: boolean) => {
    if (flag) {
      onSuccess && onSuccess(selectedValue);
    }
    controlOpen && controlOpen(false);
  };

  const handleChange = (e: any) => {
    if (e) {
      const selectedLabel = showList?.[e];
      setSelectedValue(selectedLabel ?? {});
    }
  };

  return (
    <TkModal open={open} data-testid="wanModalForTest">
      <Box className="modalForPostalCode">
        <Box className="header">
          <Typography>住所検索結果</Typography>

          <Box>
            <TkButton onClick={() => handleSelectAndClose(false)}>
              キャンセル
            </TkButton>
            <TkButton
              data-testid="modalForPostalCodeSumbitButtonForTest"
              onClick={() => handleSelectAndClose(true)}
              ml
            >
              選択して閉じる
            </TkButton>
          </Box>
        </Box>

        <Box className="context">
          <Typography>
            郵便番号：
            {postcode && `${postcode.slice(0, 3)}-${postcode.slice(3)}`}
          </Typography>

          {errorMsgId && <HandleErrorMessage id={errorMsgId} />}
          {!loading && (
            <TkTable
              headList={[
                {
                  headTitle: "住所",
                },
              ]}
              bodyList={[
                [
                  {
                    children: resList.length ? (
                      <TkRadioGroup
                        data-testid="tkRadioGroupInModalForPostalCodeForTest"
                        onGetValue={handleChange}
                        lineClassName="tkModalForPostalCodeRadioGroupLine"
                        radioPropsList={resList}
                      ></TkRadioGroup>
                    ) : (
                      <Stack
                        data-testid="tkStackForTest"
                        direction="row"
                        justifyContent="center"
                      >
                        "検索結果がありません"
                      </Stack>
                    ),
                    align: "left",
                  },
                ],
              ]}
            ></TkTable>
          )}
        </Box>
      </Box>
    </TkModal>
  );
};

export default ModalForPostalCode;
